'use client';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useSwanContext } from '../../contexts/swan-provider/swan-provider.context.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { createComponent } from '../core/create-component.js';
import { Icon } from '../icon/icon.component.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * The visual style of the Link.
   * One of: "standard", "cta", "cta-left", "unstyled".
   * @default standard
   */ skin: PropTypes.oneOf([
        'standard',
        'cta',
        'cta-left',
        'unstyled'
    ]),
    /**
   * Set the width of the Link.
   * One of: "standard", "full-width".
   * @default standard
   */ width: PropTypes.oneOf([
        'standard',
        'full-width'
    ]),
    /**
   * Whether the Link's clickable target should cover the entirety of it's nearest ancestor element.
   */ covering: PropTypes.bool,
    /**
   * Whether this is a "link with icon".
   */ withIcon: PropTypes.bool,
    /**
   * @deprecated
   * `withIconSeparated` behaves the same as `withIcon`, use `withIcon` instead.
   *
   * @description
   * Whether this "link with icon" has the icon and text visually separated.
   */ withIconSeparated: deprecatedProp(PropTypes.bool, '`withIconSeparated` behaves the same as `withIcon`, use `withIcon` instead.'),
    /**
   * Sets the destination URL in case you are setting globalLinkComponent(SwanProvider) to 3rd party library's Link (Gatsby, Next.js, React router, etc.).
   */ to: PropTypes.string
};
const filteredKeysSet = new Set(Object.keys(propTypes));
filteredKeysSet.delete('to') // 'to' prop needs to be passed through to any globalLinkComponent components
;
const propKeysToRemove = Array.from(filteredKeysSet);
const Link = createComponent({
    displayName: 'Link',
    root: 'a',
    propTypes,
    propKeysToRemove,
    propConfig: {
        derivedProps () {
            return useSwanContext();
        },
        classNames (props) {
            const { skin = 'standard', width = 'standard', withIcon, withIconSeparated, covering } = props;
            const classNameSet = new Set([
                'swan-link'
            ]);
            if (skin !== 'standard') classNameSet.add(`swan-link-skin-${skin}`);
            if (width === 'full-width') classNameSet.add(`swan-link-full-width`);
            if (withIcon || withIconSeparated) classNameSet.add('swan-link-with-icon');
            if (withIconSeparated) classNameSet.add('swan-link-with-icon-separated');
            if (covering) classNameSet.add('swan-link-covering');
            return classNameSet;
        },
        processProps (props, derivedProps) {
            if (derivedProps.globalLinkComponent) {
                return _object_spread_props(_object_spread({}, props), {
                    as: props.as || props.component || derivedProps.globalLinkComponent
                });
            }
            if (!props.href && !props.as && !props.component && !props.render) {
                return _object_spread({
                    href: props.to || undefined
                }, props);
            }
            return props;
        },
        wrapChildren ({ skin }, _, node) {
            if (skin === 'cta') {
                return /*#__PURE__*/ React.createElement(React.Fragment, null, node, /*#__PURE__*/ React.createElement(Icon, {
                    iconType: "arrowRight"
                }));
            }
            if (skin === 'cta-left') {
                return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Icon, {
                    iconType: "arrowLeft"
                }), node);
            }
            return node;
        }
    }
});

export { Link };
